/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import UI from "@mile-meeting/mile-ui";
import Chart from "react-apexcharts";
import ko from "apexcharts/dist/locales/ko.json";
import { ApexOptions } from "apexcharts";
import { format } from "date-fns";
import { TDailyVisitorCount } from "types/analytics.types";

const Container = styled.div`
  width: 520px;
  height: 480px;
  background-color: var(--alias-color-accent-light-skyblue);
  border-radius: 12px;
  box-sizing: border-box;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
`;

const DailyVisitorCountBarChart = ({
  month,
  monthlyDailyVisitorCounts,
}: {
  month: number;
  monthlyDailyVisitorCounts?: TDailyVisitorCount[];
}) => {
  const [dailyVisitChartOptions, setDailyVisitChartOptions] = useState<Object | null>(null);
  const [dailyVisitSeries, setDailyVisitSeries] = useState<ApexOptions["series"]>([]);

  const monthVistDates: string[] = (monthlyDailyVisitorCounts ?? []).map((dailyVisitInfo) =>
    format(new Date(dailyVisitInfo.date), "M월 d일")
  );
  const monthScheduledVisitCounts: number[] = (monthlyDailyVisitorCounts ?? []).map(
      (dailyVisitInfo) => dailyVisitInfo.scheduledVisitCount
  );
  const monthWalkindVisitCounts: number[] = (monthlyDailyVisitorCounts ?? []).map(
      (dailyVisitInfo) => dailyVisitInfo.walkinVisitCount
  );

  let chartSeries = [
    {
      name: "사전등록",
      data: monthScheduledVisitCounts,
    },
    {
      name: "당일방문",
      data: monthWalkindVisitCounts,
    },
  ];

  let chartOptions = {
      locales: [ko],
      defaultLocale: "ko",
      chart: {
        type: "bar",
        stacked: true,
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: false, // 창 크기 변경에 의한 리렌더링 비활성화
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 5,
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: [
        "var(--global-color-skyblue-60)",
        "var(--global-color-lime-60)",
      ],
      series: chartSeries,
      grid: {
        padding: {
          right: -5,
          bottom: 0,
          left: 0,
        },
      },
      xaxis: {
        categories: monthVistDates,
        labels: {
          show: true,
          style: {
            colors: "#A0A8B0",
            fontSize: "10px",
            fontWeight: 500,
            cssClass: "apexcharts-xaxis-label",
          },
        },
      },
      yaxis: {
        labels: {
          show: true,
          style: {
            colors: "#A0A8B0",
            fontSize: "10px",
            fontWeight: 500,
            cssClass: "apexcharts-yaxis-label",
          },
          rotate: 0,
        },
      },
    toolbar: {
      show: true,
    },
    legend: {
      position: "bottom",
      fontSize: "12px",
      fontWeight: 400,
    labels: {
        useSeriesColors: false,
        offsetX: -20,
        offsetY: 3,
      },
      itemMargin: {
        horizontal: 5,
      },
    },
  };

  useEffect(() => {
    setDailyVisitSeries(chartSeries);
    setDailyVisitChartOptions(chartOptions);
  }, [monthlyDailyVisitorCounts]);

 
  return (
    <Container>
      <TitleContainer>
        <UI.Tag label={`${month}월`} type="secondary" color="skyblue" />
        <UI.Typography type="body01 normal" fontStyle="medium" color="var(--alias-color-label-normal)">
          일별 방문자 현황
        </UI.Typography>
      </TitleContainer>
      <ContentContainer>
        {dailyVisitChartOptions ? (
          <Chart
            options={dailyVisitChartOptions}
            series={dailyVisitSeries}
            type="bar"
            width={460}
            height={340}
          />
        ) : null}
      </ContentContainer>
    </Container>
  );
};

export default DailyVisitorCountBarChart;
